import axios from 'axios';

/**
 * Turning an API failure into words an author can act on. Kept apart from
 * `api.ts` (which also builds the client, the auth refresh and the query
 * cache) so the rules can be tested on their own.
 */

/**
 * The API's raw error BODY (`{ error: '<code>', ...extras }`) for branching on
 * the code — `apiErrorMessage` renders copy, this one lets a caller read
 * `error` / `learnerCount` and decide what to do.
 */
export function apiErrorPayload(e: unknown): { error?: string; [k: string]: unknown } | undefined {
  if (axios.isAxiosError(e)) {
    return e.response?.data as { error?: string; [k: string]: unknown } | undefined;
  }
  // A cancelled upload is a local Error, not a server response, but callers
  // suppress it with `meta.silentErrorCodes` like any other code — so give it
  // one. Without this the global toast fires "video_upload_cancelled" at
  // someone who just pressed Cancel.
  if (e instanceof Error && e.name === 'UploadCancelled') {
    return { error: 'video_upload_cancelled' };
  }
  return undefined;
}

/**
 * Server error codes rendered as something an author can act on.
 *
 * Without this the creator portal shows raw snake_case in a toast —
 * "video_part_missing" is not a sentence, and it does not tell anyone what to do
 * next. Codes carrying numbers (a size cap, a part list) get them from the
 * response body, so the copy states the actual limit rather than a generic one.
 */
const ERROR_COPY: Record<string, (d: Record<string, unknown>) => string> = {
  video_too_large: (d) =>
    d.maxMb != null
      ? `That video is over the ${d.maxMb} MB limit for this site.`
      : 'That video is over the size limit for this site.',
  video_size_required: () => 'That file looks empty. Pick the video again.',
  video_not_mp4: () => 'That file is not an MP4. Export as MP4 (H.264/AAC) and upload it again.',
  video_not_uploaded: () => 'The file never finished uploading. Try again.',
  video_key_mismatch: () => 'That upload does not belong to this lesson. Start it again.',
  video_upload_expired: () =>
    'This upload expired and was cleaned up. Pick the file again to start over.',
  video_part_missing: (d) =>
    `The upload is incomplete — ${Array.isArray(d.missingParts) ? `${d.missingParts.length} piece(s)` : 'some pieces'} did not arrive. Pick the same file again to finish it.`,
  video_part_size_mismatch: () =>
    'The uploaded pieces do not match the file. Pick the file again to start over.',
  video_upload_cancelled: () => 'Upload cancelled.',
  file_too_large: () => 'That file is too large.',
  file_infected: () => 'That file failed the virus scan and was not saved.',
  already_graded: () => 'This attempt was already graded — refresh to see the result.',
  creator_not_approved: () => 'Your creator account is still awaiting approval.',
  // Payouts
  amount_must_be_positive: () => 'Enter an amount above zero.',
  insufficient_balance: () => 'That is more than your available balance.',
  payout_net_non_positive: () =>
    'After fees there would be nothing left to send. Enter a larger amount.',
  payout_destination_required: () => 'Enter the account the money should go to.',
  not_cancellable: () => 'This request can no longer be cancelled.',
  // Grading and tests
  points_exceed_max: () => 'A grade cannot be more than the question is worth.',
  missing_grade: () => 'Grade every essay question before submitting.',
  attempt_not_found: () => 'That attempt no longer exists. Refresh the list.',
  practice_no_essay: () => 'A practice test takes multiple-choice questions only.',
  test_exists: () => 'This chapter already has a test.',
  test_has_no_questions: () => 'Add at least one question first.',
  chapter_not_in_course: () => 'That chapter is not part of this course.',
  // Course editing
  embed_host_not_allowed: () =>
    'Videos from that website are not allowed here. Use YouTube or Vimeo, or upload the file.',
  lesson_source_blocked: () =>
    'Videos from that website are not allowed here. Use YouTube or Vimeo, or upload the file.',
  lesson_video_not_ready: () => 'The video is still being prepared. Try again in a minute.',
  course_has_enrollments: () =>
    'Learners are enrolled in this course, so it cannot be deleted. Unpublish it instead.',
  unsupported_file_type: () => 'That kind of file is not supported here.',
  missing_file: () => 'Pick a file first.',
  storage_unavailable: () => 'Uploads are unavailable right now. Try again shortly.',
  promo_embed_invalid: () => 'That promo link is not a video that can be played here.',
  question_hidden: () => 'This question was hidden, so it takes no new replies.',
  translation_language_required: () => 'Set the language of both courses first.',
  translation_language_taken: () => 'Another course in this group already has that language.',
  translation_same_course: () => 'A course cannot be linked to itself.',
  translation_same_language: () => 'Both courses have the same language.',
  // Coupons
  coupon_products_not_owned: () => 'A coupon can only cover your own courses.',
  coupon_needs_products: () => 'Pick at least one course for this coupon.',
  coupon_needs_code: () => 'Enter a coupon code.',
  // Registration
  email_taken: () => 'An account with that email already exists. Sign in instead.',
  missing_requirement: () => 'Fill in every required item.',
  requirement_needs_file: () => 'This item needs a file.',
  requirement_is_text: () => 'This item takes text, not a file.',
};

/** "plans.0.price" -> "Price": the last named segment of a validation path. */
function fieldLabel(path: (string | number)[] | undefined): string {
  const named = (path ?? []).filter((p): p is string => typeof p === 'string');
  const last = named[named.length - 1];
  if (!last) return 'Value';
  const words = last.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase();
}

/** "coupon_user_limit" -> "Coupon user limit." for a code with no copy yet. */
function codeSentence(code: string): string {
  const words = code.replace(/_/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
}

/** Dig the API's `{ error, message, issues }` out of an axios error for toasts. */
export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as
      | {
          error?: string;
          message?: string;
          issues?: Array<{ path?: (string | number)[]; message?: string }>;
        }
      | undefined;
    // A known code wins over the raw string AND over a server `message`, which
    // is written for a developer reading a log.
    if (data?.error && ERROR_COPY[data.error]) {
      return ERROR_COPY[data.error]!(data as Record<string, unknown>);
    }
    // A form error names the first bad field in words, not a dotted path.
    const first = data?.issues?.[0];
    if (first) return `${fieldLabel(first.path)}: ${first.message ?? 'is not valid'}`;
    if (data?.message) return data.message;
    if (data?.error) return codeSentence(data.error);
    if (e.message) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
